import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import IOSInstall from "../../integrate/_snippets/install-ios.mdx";
export const EnableSessionReplayDark = "https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/contents/images/docs/session-replay/enable-session-replay-in-project-settings-dark-mode.png";
export const EnableSessionReplayLight = "https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/contents/images/docs/session-replay/enable-session-replay-in-project-settings-light-mode.png";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const ProductScreenshot = makeShortcode("ProductScreenshot");
const layoutProps = {
  EnableSessionReplayDark,
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h2 {...{
      "id": "step-one-add-posthog-to-your-app"
    }}>{`Step one: Add PostHog to your app`}</h2>


    <IOSInstall mdxType="IOSInstall" />
    <blockquote>
      <ul parentName="blockquote">
        <li parentName="ul">{`Requires PostHog iOS SDK version >= `}<a parentName="li" {...{
            "href": "https://github.com/PostHog/posthog-ios/releases"
          }}>{`3.6.0`}</a>{`, and it's recommended to always use `}<a parentName="li" {...{
            "href": "/docs/sdk-doctor"
          }}>{`the latest version`}</a>{`.`}</li>
        <li parentName="ul">{`Session replay is currently only available on iOS. For future macOS support, please follow and upvote `}<a parentName="li" {...{
            "href": "https://github.com/PostHog/posthog-ios/issues/200"
          }}>{`this GitHub issue`}</a>{`.`}</li>
      </ul>
    </blockquote>
    <h2 {...{
      "id": "step-two-enable-session-recordings-in-your-project-settings"
    }}>{`Step two: Enable session recordings in your project settings`}</h2>
    <p>{`Enable session recordings in your PostHog `}<a parentName="p" {...{
        "href": "https://app.posthog.com/project/settings"
      }}>{`Project Settings`}</a>{`.`}</p>
    <ProductScreenshot imageLight={EnableSessionReplayLight} imageDark={EnableSessionReplayDark} alt="Enable session recordings in your PostHog'" classes="rounded" mdxType="ProductScreenshot" />
    <h2 {...{
      "id": "step-three-configure-replay-settings"
    }}>{`Step three: Configure replay settings`}</h2>
    <h3 {...{
      "id": "automatically-start-session-recordings"
    }}>{`Automatically start session recordings`}</h3>
    <p>{`Setting `}<inlineCode parentName="p">{`config.sessionReplay = true`}</inlineCode>{` to your PostHog configuration will start session recordings on SDK setup.`}</p>
    <h3 {...{
      "id": "manually-control-session-recordings"
    }}>{`Manually control session recordings`}</h3>
    <p>{`Setting `}<inlineCode parentName="p">{`config.sessionReplay = false`}</inlineCode>{` to your PostHog configuration will prevent PostHog from automatically starting session recordings on SDK setup. `}</p>
    <p>{`You can manually control when to start and stop session recordings using the following two methods:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`startSessionRecording(resumeCurrent: Bool)`}</inlineCode>
        <ul parentName="li">
          <li parentName="ul">{`Set `}<strong parentName="li">{`resumeCurrent`}</strong>{` to `}<inlineCode parentName="li">{`true`}</inlineCode>{` to resume a previous session recording (Default). `}</li>
          <li parentName="ul">{`Set `}<strong parentName="li">{`resumeCurrent`}</strong>{` to `}<inlineCode parentName="li">{`false`}</inlineCode>{` to start a new session recording. `}</li>
        </ul>
      </li>
      <li parentName="ul"><inlineCode parentName="li">{`stopSessionRecording()`}</inlineCode>{` `}
        <ul parentName="li">
          <li parentName="ul">{`Stops/pauses the current session recording.`}</li>
        </ul>
      </li>
    </ul>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Note:`}</strong>{` Calling these methods will have no effect if session recordings are disabled in your PostHog `}<a parentName="p" {...{
          "href": "https://app.posthog.com/project/settings"
        }}>{`Project Settings`}</a>{`.`}</p>
    </blockquote>
    <h2 {...{
      "id": "configuration-options"
    }}>{`Configuration options`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-swift"
      }}>{`let config = PostHogConfig(apiKey: "<ph_project_api_key>")
// Enable session recording. Requires enabling in your project settings as well.
// Default is false.
config.sessionReplay = true

// Whether text and text input fields are masked. Default is true.
// Password inputs are always masked regardless
config.sessionReplayConfig.maskAllTextInputs = true

// Whether images are masked. Default is true.
config.sessionReplayConfig.maskAllImages = true

// Enable masking of all sandboxed system views
// These may include UIImagePickerController, PHPickerViewController and CNContactPickerViewController
// Default is true.
config.sessionReplayConfig.maskAllSandboxedViews = true

// Whether network requests are captured in recordings. Default is true
// Only metric-like data like speed, size, and response code are captured. 
// No data is captured from the request or response body.
config.sessionReplayConfig.captureNetworkTelemetry = true

// Whether replays are created using high quality screenshots. Default is false.
// Required for SwiftUI.
// If disabled, replays are created using wireframes instead.
// The screenshot may contain sensitive information, so use with caution
config.sessionReplayConfig.screenshotMode = true

// Sets the minimum time interval (in seconds) between session replay snapshots.
// A higher value reduces performance impact but decreases replay smoothness. Default is 1.0s.
// Note: Previously known as \`debouncerDelay\` before version 3.21.0
config.sessionReplayConfig.throttleDelay = 1.0
`}</code></pre>
    <h2 {...{
      "id": "limitations"
    }}>{`Limitations`}</h2>
    <ul>
      <li parentName="ul">{`On iOS, minimum deployment target is `}<a parentName="li" {...{
          "href": "/docs/libraries/ios"
        }}>{`iOS13`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://developer.apple.com/xcode/swiftui/"
        }}>{`SwiftUI`}</a>{` is only supported if the `}<inlineCode parentName="li">{`screenshotMode`}</inlineCode>{` option is enabled.`}</li>
      <li parentName="ul">{`Custom views are partly supported, and only fully supported if `}<inlineCode parentName="li">{`screenshotMode`}</inlineCode>{`  is enabled.`}</li>
      <li parentName="ul">{`WebView is only supported if `}<inlineCode parentName="li">{`screenshotMode`}</inlineCode>{` is enabled. A placeholder will be shown as a fallback.`}</li>
      <li parentName="ul">{`Currently only available on iOS.`}</li>
    </ul>
    <h2 {...{
      "id": "troubleshooting"
    }}>{`Troubleshooting`}</h2>
    <ul>
      <li parentName="ul">{`Update your SDK.`}</li>
      <li parentName="ul">{`If you have enabled session replay using feature flags, the flags are evaluated on the device once the PostHog SDK starts as early as possible. The SDK might be using the cached flags from the previous SDK start. If you have changed the flag or its condition, kill and reopen the app to force a new SDK start at least once.`}
        <ul parentName="li">
          <li parentName="ul">{`This will also happen in production, and you might experience some delay for the new flag/conditions to take effect on your users. We're tracking this bug in `}<a parentName="li" {...{
              "href": "https://github.com/PostHog/posthog-ios/issues/263"
            }}>{`issue #263`}</a>{`.`}</li>
          <li parentName="ul">{`Session replay feature flag evaluation does not capture `}<inlineCode parentName="li">{`$feature_flag_called`}</inlineCode>{` events, so the `}<inlineCode parentName="li">{`Usage`}</inlineCode>{` tab on the feature flag page won't show anything. We're tracking this feature request in `}<a parentName="li" {...{
              "href": "https://github.com/PostHog/posthog-android/issues/250"
            }}>{`issue #250`}</a>{`.`}</li>
        </ul>
      </li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      